/**
 * AddEditPopup component is used to add or edit a computation.
 * It handles the form data and the options.
 * Based on the editMode, it will display the form with the data to edit or the form to add a new computation.
 * It also handles:
 * - the options popup to edit the options of the computation.
 * - the confirm popup to confirm the changes.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "devextreme-react";
import {
  Form,
  SimpleItem,
  GroupItem,
  RequiredRule,
  Label,
} from "devextreme-react/form";
import PopupWrapper from "../../../../common/pyramid/PopupWrapper";
import {
  OSIRIS_COUPON_METHOD,
  OSIRIS_INTERPOLATION_PD,
} from "data/ENUM/add-options";
import ConfirmPopup from "../../../../common/pyramid/ConfirmPopup";
import OptionPopupOsiris from "../../../../common/pyramid/OptionPopupOsiris";
import { useAuth } from "providers/AuthProvider";
import { useGroupedItems } from "hooks/useGroupedItems";
import { useFilterByCurrentGroup } from "hooks/useFilterByCurrentGroup";
import useItemOwner from "hooks/useItemOwner";

export const popupConfigs = {
  add: {
    width: "auto",
    height: "auto",
    title: "New Computation",
    icon: "add",
  },
  rowEdit: {
    width: "auto",
    height: "auto",
    title: "Edit",
    icon: "edit",
  },
};

/**
 * Props:
 * - isVisible: boolean to display the popup.
 * - onConfirm: function to confirm the changes.
 * - onCancel: function to cancel the changes.
 * - datasetList: list of datasets.
 * - inputFormData: data to edit.
 * - editMode: boolean to edit or add a new computation.
 */
const AddEditPopup = ({
  isVisible,
  onConfirm,
  onCancel,
  datasetList,
  inputFormData,
  editMode,
}) => {
  const { companyList, userList } = useAuth();

  // Determine ownership of the item if edit mode is true
  const ownedItem = useItemOwner(editMode ? inputFormData : null, "group");

  // Filter datasets on the current group
  const datasetsFilteredByGroup = useFilterByCurrentGroup(datasetList);
  const datasets = editMode ? datasetList : datasetsFilteredByGroup;

  // Group datasets by user et affichage du groupe
  const groupedDatasets = useGroupedItems(datasets, userList, companyList);

  /**
   * Get the initial data for the form.
   * If the editMode is true, return the inputFormData.
   * Otherwise, return the initial data for the form.
   * @returns {Object} The initial data for the form.
   */
  const getInitialData = () => {
    if (editMode) {
      return inputFormData;
    } else {
      return {
        description: "",
        createdDate: null,
        datasetId: null,
        couponMethod: null,
        interpolationType: null,
      };
    }
  };

  /* State variables */
  const [popupStates, setPopupStates] = useState({
    isConfirmVisible: false,
    isOptionVisible: false,
  });
  const [localData, setLocalData] = useState(getInitialData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  // Saving the current config by memoization
  const currentConfig = useMemo(
    () => (editMode ? popupConfigs.rowEdit : popupConfigs.add),
    [editMode]
  );

  useEffect(() => {
    setLocalData({ ...inputFormData });
  }, [inputFormData]);

  // Adding an useEffect to update the date when the dataset changes
  useEffect(() => {
    if (localData.datasetId) {
      const selectedDataset = datasetList.find(
        (dataset) => dataset.id === localData.datasetId
      );
      if (selectedDataset) {
        setLocalData((prev) => ({
          ...prev,
          datasetCreatedDate: selectedDataset.evaluationDate,
        }));
      }
    }
  }, [localData.datasetId, datasetList]);

  /**
   * Handles the cancel button click.
   */
  const handleCancel = () => {
    if (isSubmitting) return;

    if (editMode) {
      setLocalData({ ...inputFormData });
      formRef.current.instance().clear();
    } else {
      setLocalData(getInitialData());
      formRef.current.instance().clear();
    }
    onCancel();
  };

  /**
   * Handles the confirm button click. Verifies the form data and confirms the changes.
   */
  const handleConfirm = () => {
    if (isSubmitting) return;

    const formInstance = formRef.current.instance();
    const editableFields = [];

    if (editMode) {
      editableFields.push("description");
    } else {
      editableFields.push(
        "description",
        "datasetId",
        "couponMethod",
        "interpolationType"
      );
    }

    const validationResult = formInstance.validate(editableFields);

    if (validationResult.isValid) {
      if (!editMode && !hasOptions()) {
        setPopupStates((prev) => ({ ...prev, isConfirmVisible: true }));
      } else {
        confirmAndReset(localData);
      }
    } else {
      console.log("Invalid fields:", validationResult.brokenRules);
    }
  };

  // Checking if the options are selected
  const hasOptions = () => {
    return localData.Bucket === true || localData.ActuarialRate === true;
  };

  const confirmAndReset = async (data) => {
    setIsSubmitting(true);
    try {
      await onConfirm(data);
      formRef.current.instance().resetValues();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmYes = () => {
    if (isSubmitting) return;
    confirmAndReset(localData);
    setPopupStates((prev) => ({ ...prev, isConfirmVisible: false }));
  };

  const handleFieldChange = (e) => {
    setLocalData({ ...localData, [e.dataField]: e.value });
  };

  /**
   * Handles the options confirm button click.
   * @param {Object} updatedData - The updated data.
   */
  const handleOptionsConfirm = (updatedData) => {
    setLocalData((prevData) => {
      const newData = {
        ...prevData,
        ...updatedData,
      };
      return newData;
    });
    setPopupStates((prev) => ({ ...prev, isOptionVisible: false }));
  };

  /**
   * Handles the options cancel button click.
   * @param {Object} result - The result object with keepData property.
   */
  const handleOptionsCancel = (result) => {
    setPopupStates((prev) => ({ ...prev, isOptionVisible: false }));
    if (result && result.keepData === false) {
      setLocalData((prevData) => {
        const {
          Bucket,
          bucketRules,
          ActuarialRate,
          actuarialRateRules,
          ...remainingData
        } = prevData;
        return remainingData;
      });
    }
  };

  return (
    <>
      <PopupWrapper
        {...currentConfig}
        isVisible={isVisible}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        isConfirmDisabled={isSubmitting || (editMode && !ownedItem)}
        isCancelDisabled={isSubmitting}
        showLoading={isSubmitting}
      >
        <Form
          formData={localData}
          ref={formRef}
          onFieldDataChanged={handleFieldChange}
        >
          <GroupItem caption="Informations">
            <SimpleItem
              dataField="description"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: editMode && !ownedItem,
                placeholder: "Enter description",
                onValueChanged: (e) =>
                  setLocalData({ ...localData, description: e.value }),
              }}
              isRequired={true}
            >
              <Label text="Description" />
              <RequiredRule />
            </SimpleItem>

            <SimpleItem
              dataField="datasetCreatedDate"
              name="createdDate"
              editorType="dxDateBox"
              editorOptions={{
                readOnly: true,
                width: "34%",
                displayFormat: "yyyy-MM-dd",
                value: localData.datasetCreatedDate,
              }}
            >
              <Label text="Evaluation Date" />
            </SimpleItem>
          </GroupItem>

          <GroupItem caption="Computation" colCount={2}>
            <SimpleItem
              dataField="datasetId"
              colSpan={2}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: groupedDatasets,
                displayExpr: "description",
                valueExpr: "id",
                searchEnabled: true,
                grouped: true,
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Dataset" />
            </SimpleItem>

            <SimpleItem
              dataField="couponMethod"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: OSIRIS_COUPON_METHOD,
                displayExpr: "label",
                valueExpr: "id",
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="ECL Coupon Method" />
            </SimpleItem>

            <SimpleItem
              dataField="interpolationType"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: OSIRIS_INTERPOLATION_PD,
                displayExpr: "label",
                valueExpr: "id",
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Interpolation PD/LGD" />
            </SimpleItem>

            {editMode && (
              <>
                <SimpleItem
                  dataField="userId"
                  editorType="dxSelectBox"
                  editorOptions={{
                    readOnly: true,
                    dataSource: userList,
                    displayExpr: "displayName",
                    valueExpr: "id",
                  }}
                >
                  <Label text="Created by user" />
                </SimpleItem>

                <SimpleItem
                  dataField="groupId"
                  editorType="dxSelectBox"
                  editorOptions={{
                    readOnly: true,
                    dataSource: companyList,
                    displayExpr: "displayName",
                    valueExpr: "id",
                  }}
                >
                  <Label text="Created by company" />
                </SimpleItem>

                <SimpleItem
                  dataField="createdAt"
                  editorType="dxDateBox"
                  editorOptions={{
                    readOnly: true,
                    displayFormat: "yyyy-MM-dd",
                    value: inputFormData.createdAt,
                  }}
                >
                  <Label text="Creation Date" />
                </SimpleItem>

                <SimpleItem
                  dataField="updatedAt"
                  editorType="dxDateBox"
                  editorOptions={{
                    readOnly: true,
                    displayFormat: "yyyy-MM-dd",
                    value: inputFormData.updatedAt,
                  }}
                >
                  <Label text="Modification Date" />
                </SimpleItem>
              </>
            )}
            <SimpleItem>
              <Label text="Options" />
              <Button
                text="Edit"
                width="auto"
                type="default"
                icon="edit"
                stylingMode="outlined"
                className="mt-2"
                onClick={() =>
                  setPopupStates((prev) => ({ ...prev, isOptionVisible: true }))
                }
              />
            </SimpleItem>
          </GroupItem>
        </Form>
      </PopupWrapper>

      <ConfirmPopup
        isVisible={popupStates.isConfirmVisible}
        onNo={() =>
          setPopupStates((prev) => ({ ...prev, isConfirmVisible: false }))
        }
        onYes={handleConfirmYes}
      />

      <OptionPopupOsiris
        isVisible={popupStates.isOptionVisible}
        onClose={() =>
          setPopupStates((prev) => ({ ...prev, isOptionVisible: false }))
        }
        onCancel={(result) => handleOptionsCancel(result)}
        onConfirm={handleOptionsConfirm}
        data={localData}
      />
    </>
  );
};

export default AddEditPopup;
